import { getThemeSetting } from '@infinitro/config';
import { appMode, getKeyValue, loggedIn, ModalDialog, safeToReload, Stage, stage, stateOpenModal, storeKeyValue } from '@infinitro/global';
import { get, writable } from 'svelte/store';
export const app = import.meta.env.VITE_APP_NAME?.split('/').pop();
export const version = import.meta.env.VITE_APP_VERSION;
export const build = Number(version?.split('.')?.pop() ?? 0);
export const currentVersion = writable(version);
export const updateVersion = writable('');
let newBuild = -1;
let newVersion = '';
let isLoggedIn = undefined;
loggedIn.subscribe((value) => {
    isLoggedIn = value;
});
const INITIAL_DELAY = stage !== Stage.Prod ? 100 : 200; // (initial delay before checking for updates)
const FREQUENCY = stage !== Stage.Prod ? 1000 * 15 : 1000 * 60; // (how often to check for updates)
const TIMEOUT = stage !== Stage.Prod ? 1000 * 60 * 10 : 1000 * 60 * 60; // (timeout before allowing another attempt or asking again)
const DELAY = stage != Stage.Prod ? 1000 * 5 : 1000 * 30; // (delay before notifying, allows time for Cloudfront to invalidate)
const sleep = (ms = 0) => new Promise((resolve) => setTimeout(resolve, ms));
// no "Update Available" modal — apply the update in the background once the tab
// is backgrounded, so the visible tab never reloads out from under the user.
const silentAutoUpdate = getThemeSetting().ui?.updates?.silentAutoUpdate ?? false;
const shouldUpdate = async () => {
    try {
        if (!app || !version || !build)
            return false;
        if (newBuild <= 0)
            return false;
        if (build === newBuild)
            return false;
        const lastUpgradeAttemptBuild = await getKeyValue(`${app}:lastUpgradeAttemptBuild`);
        const lastUpgradeAttemptDate = await getKeyValue(`${app}:lastUpgradeAttemptDate`);
        if (lastUpgradeAttemptBuild &&
            lastUpgradeAttemptDate &&
            Number(lastUpgradeAttemptBuild) === newBuild) {
            // prevent another upgrade retry attempt until timeout expires
            if (Number(lastUpgradeAttemptDate) > new Date().getTime() - TIMEOUT) {
                // console.log(
                // 	`will retry again in ${Math.floor((TIMEOUT + Number(lastUpgradeAttemptDate) - new Date().getTime()) / 1000 / 60)} minutes`
                // );
                return false;
            }
        }
        return true;
    }
    catch (e) {
        console.log(`error evaluating shouldUpdate: ${e}`);
        return false;
    }
};
const getNewBuild = async () => {
    try {
        if (window?.location?.hostname?.includes('localhost'))
            return;
        const res = await fetch((isLoggedIn ? `/${appMode}/version.json?` : '/version.json?') +
            new Date().getTime().toString());
        const ver = await res.json();
        newVersion = ver?.version ?? '';
        newBuild = Number(ver?.version?.split('.')?.pop() ?? 0);
        updateVersion.set(newVersion);
    }
    catch {
        // empty // may have network error, do not log
    }
};
const notifyUpdateAvailable = async () => {
    stateOpenModal.set(ModalDialog.Update);
};
export const doUpdate = async () => {
    try {
        console.log(`attempting update from ${build} to ${newBuild}...`);
        // store update attempt date so we can implement a retry timeout
        await storeKeyValue(`${app}:lastUpgradeAttemptBuild`, newBuild.toString());
        await storeKeyValue(`${app}:lastUpgradeAttemptDate`, new Date().getTime().toString());
        // do upgrade
        if ('caches' in window) {
            try {
                // Clear all caches
                const cacheNames = await caches.keys();
                await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
                // Reload the page
                // @ts-expect-error(TS2339)
                window.location.reload(true);
            }
            catch (err) {
                console.log('Cache clearing failed:', err);
                // Fallback to simple reload
                // @ts-expect-error(TS2339)
                window.location.reload(true);
            }
        }
        else {
            // Fallback for browsers without Cache API support
            // @ts-expect-error(TS2339)
            window.location.reload(true);
        }
    }
    catch (e) {
        console.log(`error attempting update: ${e}`);
    }
};
export const dismissUpdate = async () => {
    await storeKeyValue(`${app}:lastUpgradeAttemptBuild`, newBuild.toString());
    await storeKeyValue(`${app}:lastUpgradeAttemptDate`, new Date().getTime().toString());
    console.log(`dismissed ${stage} update`);
};
const checkForUpdates = async () => {
    if (isLoggedIn === undefined)
        return;
    await getNewBuild();
    if (silentAutoUpdate)
        return; // applied via the visibilitychange listener instead
    if (await shouldUpdate()) {
        await sleep(DELAY);
        // Not safe right now (game/sports/modal/form in progress) — retry on the next poll,
        // shouldUpdate() stays true until an attempt is actually stored (doUpdate/dismissUpdate).
        if (!get(safeToReload))
            return;
        await notifyUpdateAvailable();
    }
};
// Fires only when the user backgrounds the tab (switches away/minimizes) — never while they're
// looking at it. Re-checks visibility after DELAY in case they came back before it elapsed.
const onVisibilityChange = async () => {
    if (document.visibilityState !== 'hidden')
        return;
    if (!(await shouldUpdate()))
        return;
    await sleep(DELAY);
    if (document.visibilityState !== 'hidden')
        return;
    // Same "not safe right now" check as the modal flow (game/sports/modal/form in progress in
    // the background) — retry on the next visibilitychange, shouldUpdate() stays true.
    if (!get(safeToReload))
        return;
    await doUpdate();
};
if (silentAutoUpdate && typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
}
setTimeout(async () => await checkForUpdates(), INITIAL_DELAY);
setInterval(async () => await checkForUpdates(), FREQUENCY);
